"use client";

import {
  calculateMeasurementServicePrice,
  formatPriceWithCurrency,
  type MeasurementServicePricingConfig,
} from "@/lib/pricing";
import { Check } from "lucide-react";
import { useTranslations } from "next-intl";
import { ComponentProps, useMemo } from "react";
import TitleH3 from "../fonts/TitleH3";
import MainLink from "../navigation/MainLink";

interface ResultProps {
  options: string[];
  propertyType: "singleFamilyHouse" | "multiFamilyHouse";
  floors: number;
  squareMeter: number;
  livingUnits: number;
  additionalLivingSpaceCalculation: boolean;
  cutNeeded: boolean;
  dwgFile: boolean;
  extraViews: string;
  matterport: string;
  pricingConfig?: MeasurementServicePricingConfig;
  orderHref: ComponentProps<typeof MainLink>["href"];
}

const Result = ({
  options,
  pricingConfig,
  orderHref,
  ...data
}: ResultProps) => {
  const t = useTranslations("measurementService.priceCalculation");
  const tC = useTranslations("common");

  // Calculate price based on all parameters
  const priceCalculation = useMemo(() => {
    return calculateMeasurementServicePrice(
      {
        propertyType: data.propertyType,
        floors: data.floors,
        squareMeter: data.squareMeter,
        livingUnits: data.livingUnits,
        additionalLivingSpaceCalculation: data.additionalLivingSpaceCalculation,
        cutNeeded: data.cutNeeded,
        dwgFile: data.dwgFile,
        extraViews: data.extraViews,
        matterport: data.matterport,
      },
      pricingConfig,
    );
  }, [
    data.additionalLivingSpaceCalculation,
    data.cutNeeded,
    data.dwgFile,
    data.extraViews,
    data.floors,
    data.livingUnits,
    data.matterport,
    data.propertyType,
    data.squareMeter,
    pricingConfig,
  ]);

  return (
    <div className="m-auto flex max-w-2xl flex-col gap-5">
      <div className="text-primary flex flex-col items-center gap-5 text-center">
        <span className="font-medium">{t("result.title")}</span>
        <TitleH3
          title={`${formatPriceWithCurrency(priceCalculation.total)}  ${t("result.plusTax")}`}
        />
        <span>{t("result.description")}</span>

        <div className="flex flex-col gap-1">
          <span className="text-center font-medium">{t("result.options")}</span>
          <div className="flex flex-col items-start gap-0">
            {/* Options mappen */}
            {options.length === 0
              ? t("no")
              : options.map((option, i) => {
                  return (
                    <div key={i} className="flex items-center gap-1">
                      <Check size={18} strokeWidth={3} />
                      <span>{t(`${option}`)}</span>
                    </div>
                  );
                })}
          </div>
        </div>
      </div>
      <span className="text-primary">{t("result.info")}</span>

      {/* The order itself is placed in the customer portal — same as the
          dimensioned and sales-optimized services, down to the label: this
          button opens the order wizard, it does not itself place a binding
          order (ticket #887). */}
      <div className="flex flex-col items-center">
        <MainLink
          className="max-sm:w-full"
          href={orderHref}
          title={tC("nowOrder")}
        />
      </div>
    </div>
  );
};

export default Result;
